/**
 * MossMatrix helper, Phase A: local knowledge base (no external AI).
 * Phase B: replace findChatbotResponse() internals with a fetch() to a Netlify
 * function (e.g. /.netlify/functions/chat) that calls your chosen model; keep the same return shape.
 */

export const MOSSMATRIX_CONTACT_EMAIL = "info@mossmatrix.com";

export const CHATBOT_GREETING =
  "Hi, I’m the MossMatrix helper. Ask me about sea moss, our jars and pouches, custom blends, storage, or UK delivery. You can also explore Build, Shop, Ingredients, FAQ, or Contact from the links below, or message us at info@mossmatrix.com.";

/** Shown under the opening message to guide navigation (same shape as answer links). */
export const CHATBOT_GREETING_LINKS = [
  { label: "Build your blend", path: "/#builder" },
  { label: "Shop", path: "/shop" },
  { label: "Ingredients", path: "/ingredients" },
  { label: "FAQ", path: "/faq" },
  { label: "Storage guide", path: "/learn/how-to-store-sea-moss-gel" },
  { label: "Contact", path: "/contact" },
];

export const CHATBOT_QUICK_PROMPTS = [
  "How much should I take?",
  "Gold or Purple?",
  "Can I add herbs?",
  "How do I store it?",
  "Do you deliver outside the UK?",
  "Can children take sea moss?",
  "Why no fruit blends?",
];

/** @typedef {{ label: string, path?: string, href?: string, external?: boolean }} ChatbotLink */

/**
 * @typedef {{ id: string, triggers: string[], answer: string, links?: ChatbotLink[] }} KnowledgeEntry
 */

/** @type {KnowledgeEntry[]} */
export const CHATBOT_KNOWLEDGE = [
  {
    id: "what-is-gel",
    triggers: ["what is sea moss", "what is gel", "sea moss gel", "basics", "what do you sell"],
    answer:
      "Sea moss gel is wildcrafted sea moss blended with spring water into a smooth, spoonable food. Many customers stir a small spoonful into drinks, oats, or simple recipes as part of a balanced routine. It is a food product, not a medicine.",
    links: [
      { label: "How to use", path: "/learn/how-to-use-sea-moss-gel" },
      { label: "Ingredients", path: "/ingredients" },
    ],
  },
  {
    id: "how-much-serving",
    triggers: [
      "how much should i take",
      "how much take",
      "dosage",
      "serving",
      "how many spoon",
      "how much per day",
    ],
    answer:
      "We do not give a fixed medical dose. Many customers start with a small spoonful and adjust to taste as part of their usual diet. If you are pregnant, on medication, have thyroid concerns, or iodine sensitivity, please check with a GP, pharmacist, dietitian, or other healthcare professional first.",
    links: [
      { label: "How to use", path: "/learn/how-to-use-sea-moss-gel" },
      { label: "FAQ", path: "/faq" },
    ],
  },
  {
    id: "gold-vs-purple",
    triggers: ["gold or purple", "gold vs purple", "difference gold", "which base", "choose base"],
    answer:
      "Gold is our classic, mild, mineral-rich base for everyday routines. Purple Sea Moss is our premium, higher-priced base with a deeper colour and richer character. Both are foods: choose by taste and budget, not as a medical upgrade.",
    links: [
      { label: "Gold vs Purple", path: "/learn/gold-vs-purple-sea-moss" },
      { label: "Build your blend", path: "/#builder" },
    ],
  },
  {
    id: "why-purple-more",
    triggers: ["why purple", "purple cost", "purple more expensive", "purple price"],
    answer:
      "Purple Sea Moss is our premium base, so jars and pouches cost more than gold. Many people love gold for daily use; purple is there when you want that bolder profile and are happy to spend a bit more.",
    links: [{ label: "Shop", path: "/shop" }],
  },
  {
    id: "trial-pouch",
    triggers: ["trial pouch", "200ml", "250ml", "foil pouch", "pouch trial", "zip pouch", "what is the trial pouch"],
    answer:
      "Our 200ml foil trial pouches are plain sea moss gel only: sea moss base and spring water, no botanical add-ins. They are a simple way to try Moss Matrix before moving up in size. (We may still list a legacy 250ml pouch while stock transitions.) Keep refrigerated. UK delivery only.",
    links: [
      { label: "Shop", path: "/#shop" },
      { label: "Build your blend", path: "/#builder" },
    ],
  },
  {
    id: "pouch-plain",
    triggers: ["pouch plain", "herbs in pouch", "custom pouch", "pouch herbs"],
    answer:
      "200ml trial pouches stay plain only. On 500ml and 1 litre you can choose jar or pouch in the builder and add up to three powdered botanicals for a custom blend.",
    links: [{ label: "Shop", path: "/#shop" }, { label: "Builder", path: "/#builder" }],
  },
  {
    id: "jar-300-plain",
    triggers: ["300ml", "300 ml", "small jar plain"],
    answer:
      "300ml is plain sea moss gel only, in either a glass jar or a foil pouch. Custom blends with up to three powdered botanicals start from 500ml and 1 litre in the builder.",
    links: [{ label: "Shop", path: "/#shop" }, { label: "Build", path: "/#builder" }],
  },
  {
    id: "custom-500",
    triggers: ["500ml custom", "500 custom", "custom 500"],
    answer:
      "500ml can be plain or custom. Custom is priced from the plain base for that jar or pouch, then £1.50 for each powdered botanical you add (up to three). Pick gold or purple, packaging, and botanicals in the builder.",
    links: [{ label: "Build your blend", path: "/#builder" }, { label: "Ingredients", path: "/ingredients" }],
  },
  {
    id: "custom-1l",
    triggers: ["1 litre custom", "1 liter custom", "1l custom", "litre custom"],
    answer:
      "1 litre matches 500ml: plain base price, or add up to three powdered botanicals at £1.50 each in the builder. Jar or pouch.",
    links: [{ label: "Build your blend", path: "/#builder" }],
  },
  {
    id: "builder-curated",
    triggers: [
      "curated",
      "launch collection",
      "launch menu",
      "why not all herbs",
      "every ingredient",
      "not in builder",
      "missing herb",
      "suggest botanical",
      "library vs builder",
      "ingredient library builder",
    ],
    answer:
      "The builder uses a curated launch collection of powdered botanicals, not every page in the ingredient library. That keeps production consistent and your gel fresh and straightforward to prepare. Plain sea moss is still the simplest and best-value option. If something you enjoy reading about is not on the menu yet, you can suggest it for future planning through our contact form. We only ship blends built from the options listed in the builder, and there is no paid add-on for unlisted herbs.",
    links: [
      { label: "Suggest a botanical", path: "/contact?topic=suggest-botanical" },
      { label: "Build your blend", path: "/#builder" },
      { label: "Ingredient library", path: "/ingredients" },
    ],
  },
  {
    id: "max-herbs",
    triggers: ["how many herbs", "max herbs", "three herbs", "3 herbs", "can i add herbs", "add herbs", "botanicals"],
    answer:
      "You can add up to three powdered botanicals on 500ml and 1 litre custom gels. Each one adds £1.50 to the plain base price for that size. Shipped custom gels use powders only (not fresh fruit or veg) so the gel travels better. The builder lists a curated launch menu; the ingredient library has more to read for ideas, but checkout only uses what you pick from that list.",
    links: [{ label: "Ingredient library", path: "/ingredients" }, { label: "Build", path: "/#builder" }],
  },
  {
    id: "uk-only",
    triggers: ["deliver outside", "international", "ship abroad", "europe", "usa", "outside uk", "non uk"],
    answer:
      "We deliver within the UK only. We do not ship outside the United Kingdom at the moment. If that ever changes, we will say so clearly on the site.",
    links: [{ label: "FAQ", path: "/faq" }, { label: "Contact", path: "/contact" }],
  },
  {
    id: "dispatch-time",
    triggers: ["how long dispatch", "when ship", "processing time", "how fast", "dispatch"],
    answer:
      "Gel is made fresh to order. We usually prepare and dispatch within about 1 to 3 working days, then it goes out on UK tracked delivery. At busy times we may use the full window. UK delivery only.",
    links: [{ label: "FAQ", path: "/faq" }],
  },
  {
    id: "busy-48h",
    triggers: ["busy", "48 hours", "48h", "preparation"],
    answer:
      "When we are busy we still aim for roughly 1 to 3 working days from order to dispatch. We would rather batch carefully than rush gel out the door.",
  },
  {
    id: "fridge",
    triggers: ["refrigerat", "fridge", "keep cold", "storage", "how do i store"],
    answer:
      "Keep gel refrigerated and use clean spoons, like any fresh food. See our storage guide for freezing tips (not in glass jars). If anything looks or smells off, do not eat it.",
    links: [{ label: "Storage guide", path: "/learn/how-to-store-sea-moss-gel" }],
  },
  {
    id: "freshness-window",
    triggers: ["how long last", "shelf life", "expires", "freshness"],
    answer:
      "How long it stays at its best depends on storage and how often you open it. Keep it refrigerated with clean utensils and follow any dates or notes on your pack. Email us if you are unsure.",
  },
  {
    id: "freezing",
    triggers: ["freeze", "freezing", "frozen"],
    answer:
      "Many customers freeze portions in freezer-safe containers for longer storage. Do not freeze in glass jars: liquid expands and the glass can crack. Thaw in the fridge. General food tips only, not medical advice.",
    links: [{ label: "Storage guide", path: "/learn/how-to-store-sea-moss-gel" }],
  },
  {
    id: "how-to-use",
    triggers: ["how to use", "eat sea moss", "spoon", "smoothie", "oats", "tea", "active routine", "active routines"],
    answer:
      "Lots of people stir a spoonful into cooled drinks, oats, plant milks, or simple blends. Many customers start with a small spoonful and adjust to taste. This is routine food use, not medical advice.",
    links: [
      { label: "How to use", path: "/learn/how-to-use-sea-moss-gel" },
      { label: "Active routines", path: "/learn/sea-moss-for-active-routines" },
    ],
  },
  {
    id: "active-routines",
    triggers: ["gym", "workout", "training", "active day", "protein shake routine"],
    answer:
      "Fridge-ready gel often fits kitchen habits around walk or training days — morning spoons, home smoothies, clean fridge storage. We do not sell it as a performance supplement or make treatment claims.",
    links: [
      { label: "Active routines guide", path: "/learn/sea-moss-for-active-routines" },
      { label: "How to use", path: "/learn/how-to-use-sea-moss-gel" },
    ],
  },
  {
    id: "protein-shake",
    triggers: ["protein", "shake", "whey", "creatine", "gym drink"],
    answer:
      "Some people stir gel into shakes as part of a normal diet. We do not sell it as a supplement or make performance or treatment claims.",
    links: [{ label: "Active routines guide", path: "/learn/sea-moss-for-active-routines" }],
  },
  {
    id: "herbs-only",
    triggers: [
      "fruit in gel",
      "vegetable",
      "smoothie in jar",
      "fresh fruit",
      "why no fruit",
      "fruit blends",
      "no fruit blends",
    ],
    answer:
      "Our custom gels use powdered botanicals because they travel better and help keep the product more stable. Fresh fruit and vegetables are not added to shipped gels because they can ferment, shorten shelf life, and affect delivery quality. You can always add gel to fruit or smoothies at home.",
    links: [{ label: "Ingredient library", path: "/ingredients" }, { label: "FAQ", path: "/faq" }],
  },
  {
    id: "children",
    triggers: ["children", "child", "kids", "five year", "5 year", "toddler", "baby"],
    answer:
      "Sea moss can be iodine-rich, so we do not give serving amounts for children. For any child, please speak with a GP, paediatrician, pharmacist, or dietitian first, especially with allergies, thyroid issues, or medication. You can also message us at info@mossmatrix.com.",
    links: [{ label: "Contact us", path: "/contact" }, { label: "Email", href: `mailto:${MOSSMATRIX_CONTACT_EMAIL}` }],
  },
  {
    id: "pregnancy",
    triggers: ["pregnant", "pregnancy", "breastfeeding", "nursing"],
    answer:
      "If you are pregnant, trying to conceive, or breastfeeding, please speak with your GP, midwife, or pharmacist before adding sea moss gel. It can be iodine-rich. This is general guidance, not personal medical advice.",
    links: [{ label: "Contact us", path: "/contact" }],
  },
  {
    id: "thyroid",
    triggers: ["thyroid", "iodine", "bladderwrack", "hormone"],
    answer:
      "Sea moss can contain iodine. If you have thyroid disease, take thyroid medication, or were told to limit iodine, speak with your clinician before use. We cannot give dosing or treatment advice here.",
    links: [{ label: "FAQ", path: "/faq" }],
  },
  {
    id: "medication-allergy",
    triggers: ["medication", "medicine", "allergy", "allergic", "interaction"],
    answer:
      "If you take prescription medicines, have allergies, or manage a health condition, speak with a GP, pharmacist, or dietitian before trying sea moss gel. We cannot assess individual risk in chat. You can also message us at info@mossmatrix.com.",
    links: [{ label: "Contact us", path: "/contact" }, { label: "Email", href: `mailto:${MOSSMATRIX_CONTACT_EMAIL}` }],
  },
  {
    id: "blood-liver-chronic",
    triggers: ["blood thinner", "warfarin", "liver", "long term", "chronic", "health condition"],
    answer:
      "For blood thinners, liver conditions, long-term illness, or complex health questions, please speak with a GP, pharmacist, or specialist. We only share general food and product information here, not treatment advice.",
    links: [{ label: "Contact", path: "/contact" }],
  },
  {
    id: "contact",
    triggers: ["contact", "email you", "speak to human", "talk to", "returns", "refund", "problem order"],
    answer:
      "For anything specific or sensitive, email us and a human will reply. A short order reference helps if you have one. You can also message us at info@mossmatrix.com.",
    links: [
      { label: "Contact page", path: "/contact" },
      { label: `Email ${MOSSMATRIX_CONTACT_EMAIL}`, href: `mailto:${MOSSMATRIX_CONTACT_EMAIL}` },
    ],
  },
  {
    id: "pricing-best",
    triggers: ["price", "pricing", "cost", "cheap", "ebay", "best price"],
    answer:
      "Our listed prices live on the shop and in the builder summary on MossMatrix.com. Guest checkout stays simple with no customer login.",
    links: [{ label: "Shop", path: "/shop" }],
  },
  {
    id: "routines",
    triggers: ["routine", "blend idea", "suggested", "which blend", "choose blend"],
    answer:
      "We share blend ideas as gentle inspiration only, not prescriptions. Browse blend routines and ingredients, then open the builder when something feels right.",
    links: [
      { label: "Blend routines", path: "/blends" },
      { label: "Build your blend", path: "/#builder" },
    ],
  },
  {
    id: "disclaimer",
    triggers: ["disclaimer", "medical", "cure", "safe", "fda"],
    answer:
      "MossMatrix gel is a food product. We do not claim it cures, treats, prevents, or reverses any disease, or that it heals you or guarantees results. If you feel unwell or unsure, speak with a qualified health professional.",
    links: [{ label: "FAQ", path: "/faq" }],
  },
];

const FALLBACK_ANSWER = `I’m not sure I have a ready answer for that in here. Email ${MOSSMATRIX_CONTACT_EMAIL} and we will help you directly.`;

/**
 * @param {string} raw
 */
function normalizeQuery(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Phase A: keyword / trigger match. Phase B: call Netlify AI endpoint here instead, same return type.
 * @param {string} userMessage
 * @returns {{ answer: string, links?: ChatbotLink[], matchedId?: string | null }}
 */
export function findChatbotResponse(userMessage) {
  const q = normalizeQuery(userMessage);
  if (!q) {
    return { answer: CHATBOT_GREETING, links: undefined, matchedId: null };
  }

  let best = /** @type {KnowledgeEntry | null} */ (null);
  let bestScore = 0;

  for (const entry of CHATBOT_KNOWLEDGE) {
    let score = 0;
    for (const t of entry.triggers) {
      const nt = t.toLowerCase();
      if (q.includes(nt)) score += Math.min(12, 3 + Math.floor(nt.length / 4));
    }
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  if (best && bestScore >= 4) {
    return { answer: best.answer, links: best.links, matchedId: best.id };
  }

  /** Loose single-token help */
  const tokens = q.split(" ").filter((w) => w.length > 2);
  for (const entry of CHATBOT_KNOWLEDGE) {
    let hits = 0;
    for (const tok of tokens) {
      for (const t of entry.triggers) {
        if (t.toLowerCase().includes(tok) || tok.includes(t.toLowerCase())) hits++;
      }
    }
    if (hits >= 2 && (!best || hits > bestScore)) {
      best = entry;
      bestScore = hits;
    }
  }

  if (best && bestScore >= 2) {
    return { answer: best.answer, links: best.links, matchedId: best.id };
  }

  return {
    answer: FALLBACK_ANSWER,
    links: [
      { label: "Email us", href: `mailto:${MOSSMATRIX_CONTACT_EMAIL}` },
      { label: "Contact", path: "/contact" },
    ],
    matchedId: null,
  };
}
